import { put, takeLatest, select, race, take, call } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { onIdTokenChanged } from 'firebase/auth';

import { types, actions } from '../auth/refresh';
import * as loginModule from '../auth/login';

import { firebaseAuth } from '../../../../src/firebase.js';

// True only when the current session was established via Firebase
// (CustomerLogin), as opposed to a plain email/password business login.
function* isFirebaseSession() {
  const accessToken = yield select(state => state.auth.tokens.access);
  return !!firebaseAuth.currentUser && !!accessToken;
}

function createAuthChannel() {
  return eventChannel(emit => {
    const unsubscribe = onIdTokenChanged(firebaseAuth, user => {
      emit(user || null);
    });
    return () => unsubscribe();
  });
}

// Refreshes a Firebase ID token and re-runs it through loginSaga so it gets
// re-stored as the access token, same as mobile's refreshFirebaseTokenSaga.
export function* refreshFirebaseTokenSaga() {
  const authChannel = createAuthChannel();

  try {
    while (true) {
      const firebaseUser = yield take(authChannel);

      if (firebaseUser) {
        yield put(actions.refreshAuthRequest());
        const firebaseIdToken = yield call(
          [firebaseUser, 'getIdToken'],
          true,
        );

        yield put(
          loginModule.actions.login(firebaseUser.email, null, firebaseIdToken),
        );

        const { success, failure } = yield race({
          success: take(loginModule.types.LOGIN_SUCCESS),
          failure: take(loginModule.types.LOGIN_FAILURE),
        });

        if (success) {
          yield put(
            actions.refreshAuthSuccess({
              access: firebaseIdToken,
              refresh: null,
            }),
          );
        } else {
          const { error } = failure;
          yield put(actions.refreshAuthFailure(error));
        }
      }
    }
  } catch (error) {
    yield put(actions.refreshAuthFailure(error.message));
  } finally {
    authChannel.close();
  }
}

// Routes REFRESH_AUTH to the Firebase-aware refresh only when the active
// session is a Firebase one; otherwise the generic coupon-common refresh
// saga (already registered elsewhere) handles plain email/password sessions.
function* refreshAuthRouterSaga() {
  const useFirebase = yield call(isFirebaseSession);
  if (useFirebase) {
    yield call(refreshFirebaseTokenSaga);
  }
}

export default function* saga() {
  yield takeLatest(types.REFRESH_AUTH, refreshAuthRouterSaga);
}